import type { ListItem } from '@awwyeah/shared';
import { Check, GripVertical } from 'lucide-react';
import { useState } from 'react';
import { MemberAvatar, UnassignedAvatar } from './MemberAvatar';
import type { RosterMember } from '../useHouseholdRoster';

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export interface ListItemRowProps {
  item: ListItem;
  variant: 'todo' | 'shopping';
  getMember: (id: string) => RosterMember;
  meId: string | null;
  flashing?: boolean;
  reorderable?: boolean;
  onToggle: () => void;
  onDelete?: () => void;
  onAssign?: () => void;
  /** When provided, tapping the item's text edits it inline (saved on Enter/blur). */
  onTextChange?: (text: string) => void;
  onDragHandlePointerDown?: (event: React.PointerEvent) => void;
}

/**
 * One checkable row — 56px `todo` / 72px `shopping` per Component - ListItem.dc.html.
 * Attribution reads "who last touched this", not who created it (own edits show no
 * avatar — "you know what you did").
 */
export function ListItemRow({
  item,
  variant,
  getMember,
  meId,
  flashing,
  reorderable,
  onToggle,
  onDelete,
  onAssign,
  onTextChange,
  onDragHandlePointerDown,
}: ListItemRowProps): JSX.Element {
  const [editDraft, setEditDraft] = useState<string | null>(null);
  const commitEdit = (): void => {
    if (editDraft !== null && onTextChange) {
      const trimmed = editDraft.trim();
      if (trimmed && trimmed !== item.text) onTextChange(trimmed);
    }
    setEditDraft(null);
  };
  const boxSize = variant === 'shopping' ? 'h-10 w-10 rounded-lg' : 'h-[26px] w-[26px] rounded';
  const rowMinHeight = variant === 'shopping' ? 'min-h-[72px]' : 'min-h-[56px]';
  const textSize = variant === 'shopping' ? 'text-lg' : 'text-base';
  const attributedTo = item.checked ? item.checkedBy : item.updatedBy;
  const attributedAt = item.checked ? item.checkedAt : item.updatedAt;
  const showAttribution = attributedTo && attributedTo !== meId;
  const assignees = item.assigneeIds.map((id) => getMember(id));

  return (
    <div
      data-testid="list-item"
      data-item-id={item.id}
      data-checked={item.checked}
      className={`flex items-center gap-3 border-b border-line px-4 py-2 last:border-b-0 transition-colors duration-300 ease-app ${rowMinHeight} ${
        flashing ? 'bg-highlight' : item.checked ? 'bg-bg' : 'bg-surface'
      }`}
    >
      {reorderable && (
        <button
          type="button"
          aria-label="Drag to reorder"
          data-testid="reorder-handle"
          className="flex h-11 w-6 flex-shrink-0 touch-none items-center justify-center text-fg-3"
          onPointerDown={onDragHandlePointerDown}
        >
          <GripVertical size={16} />
        </button>
      )}
      <button
        type="button"
        aria-label={item.checked ? `Uncheck ${item.text}` : `Check ${item.text}`}
        data-testid="item-checkbox"
        onClick={onToggle}
        className={`flex flex-shrink-0 items-center justify-center border-2 transition-colors duration-150 ${boxSize} ${
          item.checked ? 'border-fg-3 bg-fg-3' : 'border-line-strong bg-transparent'
        }`}
      >
        {item.checked && (
          <Check size={variant === 'shopping' ? 20 : 16} className="text-surface" strokeWidth={3} />
        )}
      </button>
      <div className="min-w-0 flex-1">
        {editDraft !== null ? (
          <input
            data-testid="item-text-input"
            autoFocus
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') setEditDraft(null);
            }}
            onBlur={commitEdit}
            className={`w-full min-w-0 rounded border border-line bg-bg px-1 py-0.5 ${textSize} font-medium text-fg outline-none`}
          />
        ) : (
          <div
            className={`${textSize} font-medium ${item.checked ? 'text-fg-3 line-through' : 'text-fg'} ${
              onTextChange ? 'cursor-text' : ''
            }`}
            data-testid="item-text"
            onClick={onTextChange ? () => setEditDraft(item.text) : undefined}
          >
            {item.text}
          </div>
        )}
        {item.detail && !showAttribution && (
          <div className="text-xs text-fg-3" data-testid="item-detail">
            {item.detail}
          </div>
        )}
        {showAttribution && attributedTo && attributedAt && (
          <div className="text-xs font-semibold text-fg-3" data-testid="item-attribution">
            {getMember(attributedTo).name} {item.checked ? 'got it' : 'changed this'} ·{' '}
            {relativeTime(attributedAt)}
          </div>
        )}
      </div>
      {(assignees.length > 0 || (variant === 'todo' && !item.checked)) && (
        <button
          type="button"
          aria-label={`Assign ${item.text}`}
          data-testid="item-assign"
          onClick={onAssign}
          disabled={!onAssign}
          className="flex flex-shrink-0"
        >
          {assignees.length > 0
            ? assignees.map((member) => <MemberAvatar key={member.id} member={member} size="sm" />)
            : variant === 'todo' && !item.checked && <UnassignedAvatar size="sm" />}
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          aria-label={`Delete ${item.text}`}
          data-testid="item-delete"
          onClick={onDelete}
          className="text-xs font-semibold text-fg-3 hover:text-danger"
        >
          Delete
        </button>
      )}
    </div>
  );
}
